import { FC, useState, useEffect } from 'react';
import { MenuItem, Autocomplete, TextField } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import DatePicker from 'src/components/InputFields/DatePicker';
import TextArea from 'src/components/InputFields/TextArea';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import {
  getCustomerListBasic,
  getCustomerAddresses,
} from 'src/services/customer.service';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  FormContainer,
  ScrollableContent,
  Wrapper,
  Column,
  InnerColumn,
  SmallColumn,
  DeliveryNoteColumn,
  StyledSelect,
  StyledAutocomplete,
  StyledTextField,
  AddressCardEmpty,
  AddressIcon,
  SelectAddressText,
  AddressCardFilled,
  TitleTypography,
  SectionTitle,
  AddressText,
  AddressContactRow,
  AddressContactText,
  HiddenTitle,
  Height40Box,
  OptionalSpan,
  FieldContainer,
  ErrorAbsolute,
  Mt1Box,
  NextButton,
  NextButtonContainer,
} from './CustomerInformation.styles';

interface CustomerInformationProps {
  onNext: () => void;
}

interface Customer {
  id: string;
  name: string;
  billingAddresses?: Address[];
  deliveryAddresses?: Address[];
}

interface Address {
  id: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
  contactPerson?: string;
  isDefault?: boolean;
}

const CustomerInformation: FC<CustomerInformationProps> = ({ onNext }) => {
  const { showSnackbar } = StyledSnackbar();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [billingAddresses, setBillingAddresses] = useState<Address[]>([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState<Address[]>([]);
  const [isRestoring, setIsRestoring] = useState(true);

  const validationSchema = Yup.object({
    customerId: Yup.string().required('Customer is required'),
    billingAddressId: Yup.string().required('Billing address is required'),
    deliveryAddressId: Yup.string().required('Delivery address is required'),
    scheduledDeliveryDate: Yup.date()
      .required('Scheduled delivery date is required')
      .nullable(),
    deliveryNote: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      customerId: '',
      billingAddressId: '',
      deliveryAddressId: '',
      scheduledDeliveryDate: dayjs(),
      deliveryNote: '',
    },
    validationSchema,
    onSubmit: (values) => {
      const selectedCustomer = customers.find(
        (c) => c.id === values.customerId
      );
      const dataToSave = {
        ...values,
        customerName: selectedCustomer?.name || '',
      };
      localStorage.setItem('orderStep1', JSON.stringify(dataToSave));
      onNext();
    },
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  // load saved step1 data if present. Await restoring addresses so we don't
  // persist an intermediate empty billing/delivery id and overwrite saved data.
  useEffect(() => {
    (async () => {
      try {
        const saved = localStorage.getItem('orderStep1');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.customerId) {
            formik.setFieldValue(
              'customerId',
              String(parsed.customerId),
              false
            );
            // fetch addresses for this customer (addresses endpoint accepts customerId)
            // fetchCustomerAddresses will re-apply saved address IDs when possible
            await fetchCustomerAddresses(parsed.customerId);
          }
          if (parsed.scheduledDeliveryDate) {
            formik.setFieldValue(
              'scheduledDeliveryDate',
              dayjs(parsed.scheduledDeliveryDate),
              false
            );
          }
          if (parsed.deliveryNote) {
            formik.setFieldValue('deliveryNote', parsed.deliveryNote, false);
          }
        }
      } catch (err) {
        // ignore JSON parse errors
      } finally {
        setIsRestoring(false);
      }
    })();
  }, []);

  const fetchCustomers = async () => {
    try {
      const customersRaw = await getCustomerListBasic();
      const customersNormalized: Customer[] = (customersRaw || []).map(
        (c: any) => ({
          ...c,
          id: String(c.id),
        })
      );
      setCustomers(customersNormalized);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  useEffect(() => {
    if (formik.values.customerId) {
      const customer = customers.find((c) => c.id === formik.values.customerId);
      if (customer) {
        fetchCustomerAddresses(customer.id);
      }
    } else {
      setBillingAddresses([]);
      setDeliveryAddresses([]);
      formik.setFieldValue('billingAddressId', '');
      formik.setFieldValue('deliveryAddressId', '');
    }
  }, [formik.values.customerId]);

  const fetchCustomerAddresses = async (customerId: string) => {
    try {
      const addressesRaw = await getCustomerAddresses(customerId);

      const addresses: Address[] = (addressesRaw || []).map((a: any) => ({
        id: String(a.id),
        addressLine1: a.addressLine1 || a.addressLine || '',
        addressLine2: a.addressLine2 || a.addressLineTwo || '',
        city: a.city || '',
        state: a.state || a.region || '',
        postalCode: a.postalCode || a.postCode || '',
        country: a.country || '',
        contactPerson: a.contactPersonName || a.contactPerson || '',
        isDefault: a.isDefault || false,
      }));

      setBillingAddresses(addresses);
      setDeliveryAddresses(addresses);

      const defaultAddress = addresses.find((addr) => addr.isDefault === true);

      let shouldRestoreSaved = false;
      if (isRestoring) {
        try {
          const saved = localStorage.getItem('orderStep1');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (
              parsed.customerId &&
              String(parsed.customerId) === String(customerId)
            ) {
              shouldRestoreSaved = true;

              if (parsed.billingAddressId || parsed.billingAddressId === 0) {
                formik.setFieldValue(
                  'billingAddressId',
                  String(parsed.billingAddressId),
                  false
                );
              } else if (defaultAddress) {
                formik.setFieldValue(
                  'billingAddressId',
                  String(defaultAddress.id),
                  false
                );
              }

              if (parsed.deliveryAddressId || parsed.deliveryAddressId === 0) {
                formik.setFieldValue(
                  'deliveryAddressId',
                  String(parsed.deliveryAddressId),
                  false
                );
              } else if (defaultAddress) {
                formik.setFieldValue(
                  'deliveryAddressId',
                  String(defaultAddress.id),
                  false
                );
              }
            }
          }
        } catch (err) {
          // ignore parsing errors
        }
      }

      if (!shouldRestoreSaved && defaultAddress) {
        formik.setFieldValue(
          'billingAddressId',
          String(defaultAddress.id),
          false
        );
        formik.setFieldValue(
          'deliveryAddressId',
          String(defaultAddress.id),
          false
        );
      }
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const renderAddressCard = (
    address: Address | null,
    type: 'billing' | 'delivery'
  ) => {
    if (!address) {
      return (
        <AddressCardEmpty>
          <AddressIcon className="bx bx-buildings" />
          <SelectAddressText>Select address</SelectAddressText>
        </AddressCardEmpty>
      );
    }

    return (
      <AddressCardFilled>
        <SectionTitle>
          {type === 'billing' ? 'Billing Address' : 'Delivery Address'}
        </SectionTitle>
        {address.contactPerson && (
          <AddressContactRow>
            <AddressIcon className="bx bx-buildings" />
            <AddressContactText>{address.contactPerson}</AddressContactText>
          </AddressContactRow>
        )}
        <AddressText>
          {address.addressLine1}
          <br />
          {address.addressLine2 && `${address.addressLine2}`}
          <br />
          {address.city}
          <br />
          {address.postalCode && `${address.postalCode}`}
          <br />
          {address.country}
        </AddressText>
      </AddressCardFilled>
    );
  };

  const selectedBillingAddress = billingAddresses.find(
    (addr) => String(addr.id) === String(formik.values.billingAddressId)
  );
  const selectedDeliveryAddress = deliveryAddresses.find(
    (addr) => String(addr.id) === String(formik.values.deliveryAddressId)
  );

  // show error immediately when user selects a past date
  useEffect(() => {
    const val = formik.values.scheduledDeliveryDate;
    if (!val) return;
    const isPast = dayjs(val).isBefore(dayjs().startOf('day'));
    if (isPast) {
      // mark touched so error UI appears immediately
      formik.setFieldTouched('scheduledDeliveryDate', true, false);
    }
  }, [formik.values.scheduledDeliveryDate]);

  // persist step1 to localStorage on changes so user can come back
  useEffect(() => {
    if (isRestoring) return;
    const toSave = {
      customerId: formik.values.customerId,
      billingAddressId: formik.values.billingAddressId,
      deliveryAddressId: formik.values.deliveryAddressId,
      scheduledDeliveryDate: formik.values.scheduledDeliveryDate
        ? dayjs(formik.values.scheduledDeliveryDate).toISOString()
        : null,
      deliveryNote: formik.values.deliveryNote,
    };
    try {
      localStorage.setItem('orderStep1', JSON.stringify(toSave));
    } catch (err) {
      // ignore storage errors
    }
  }, [
    formik.values.customerId,
    formik.values.billingAddressId,
    formik.values.deliveryAddressId,
    formik.values.scheduledDeliveryDate,
    formik.values.deliveryNote,
  ]);

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <ScrollableContent>
        <Wrapper>
          <Column>
            <InnerColumn>
              <TitleTypography>Customer</TitleTypography>
              <FieldContainer>
                <StyledAutocomplete
                  options={customers}
                  getOptionLabel={(option) => option.name}
                  value={
                    customers.find((c) => c.id === formik.values.customerId) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue('customerId', newValue?.id || '');
                  }}
                  onBlur={formik.handleBlur}
                  ListboxProps={{
                    style: {
                      maxHeight: '350px',
                      overflow: 'auto',
                    },
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      name="customerId"
                      placeholder="Select customer"
                      hasValue={!!formik.values.customerId}
                      hasError={
                        formik.touched.customerId && !!formik.errors.customerId
                      }
                    />
                  )}
                />
                {formik.touched.customerId && formik.errors.customerId && (
                  <ErrorAbsolute>{formik.errors.customerId}</ErrorAbsolute>
                )}
              </FieldContainer>
            </InnerColumn>
            <SmallColumn>
              <TitleTypography>Billing Address</TitleTypography>
              <FieldContainer>
                <StyledAutocomplete
                  options={billingAddresses}
                  getOptionLabel={(option) =>
                    [
                      option.addressLine1,
                      option.addressLine2,
                      option.city,
                      option.postalCode,
                      option.country,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  }
                  value={
                    billingAddresses.find(
                      (a) => a.id === formik.values.billingAddressId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      'billingAddressId',
                      newValue?.id || ''
                    );
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.customerId}
                  ListboxProps={{
                    style: {
                      maxHeight: '350px',
                      overflow: 'auto',
                    },
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      name="billingAddressId"
                      placeholder="Select address"
                      hasValue={!!formik.values.billingAddressId}
                      hasError={
                        formik.touched.billingAddressId &&
                        !!formik.errors.billingAddressId
                      }
                    />
                  )}
                />
                {formik.touched.billingAddressId &&
                  formik.errors.billingAddressId && (
                    <ErrorAbsolute>
                      {formik.errors.billingAddressId}
                    </ErrorAbsolute>
                  )}
              </FieldContainer>
              <Mt1Box>
                {renderAddressCard(selectedBillingAddress || null, 'billing')}
              </Mt1Box>
            </SmallColumn>
          </Column>
          <Column>
            <InnerColumn>
              <HiddenTitle>&nbsp;</HiddenTitle>
              <Height40Box />
            </InnerColumn>
            <SmallColumn>
              <TitleTypography>Delivery Address</TitleTypography>
              <FieldContainer>
                <StyledAutocomplete
                  options={deliveryAddresses}
                  getOptionLabel={(option) =>
                    [
                      option.addressLine1,
                      option.addressLine2,
                      option.city,
                      option.postalCode,
                      option.country,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  }
                  value={
                    deliveryAddresses.find(
                      (a) => a.id === formik.values.deliveryAddressId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      'deliveryAddressId',
                      newValue?.id || ''
                    );
                  }}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.customerId}
                  ListboxProps={{
                    style: {
                      maxHeight: '350px',
                      overflow: 'auto',
                    },
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      name="deliveryAddressId"
                      placeholder="Select address"
                      hasValue={!!formik.values.deliveryAddressId}
                      hasError={
                        formik.touched.deliveryAddressId &&
                        !!formik.errors.deliveryAddressId
                      }
                    />
                  )}
                />
                {formik.touched.deliveryAddressId &&
                  formik.errors.deliveryAddressId && (
                    <ErrorAbsolute>
                      {formik.errors.deliveryAddressId}
                    </ErrorAbsolute>
                  )}
              </FieldContainer>
              <Mt1Box>
                {renderAddressCard(selectedDeliveryAddress || null, 'delivery')}
              </Mt1Box>
            </SmallColumn>
          </Column>
          <Column>
            <InnerColumn>
              <TitleTypography>Scheduled Delivery Date</TitleTypography>
              <FieldContainer>
                <DatePicker
                  name="scheduledDeliveryDate"
                  formikProps={formik}
                  width="100%"
                  height="40px"
                  fontSize="14px"
                  borderColor={
                    formik.touched.scheduledDeliveryDate &&
                    formik.errors.scheduledDeliveryDate
                      ? '#FF0000'
                      : '#D9F1FF'
                  }
                />
                {formik.touched.scheduledDeliveryDate &&
                  formik.errors.scheduledDeliveryDate && (
                    <ErrorAbsolute>
                      {formik.errors.scheduledDeliveryDate as string}
                    </ErrorAbsolute>
                  )}
              </FieldContainer>
            </InnerColumn>
            <DeliveryNoteColumn>
              <TitleTypography>
                Delivery Note <OptionalSpan>Optional</OptionalSpan>
              </TitleTypography>
              <TextArea
                id="deliveryNote"
                label=""
                formikProps={formik}
                placeholder="Add any special delivery instructions..."
                mt={-8}
                height="188px"
                width="100%"
                showLabel={false}
                rows={6}
              />
            </DeliveryNoteColumn>
          </Column>
        </Wrapper>
      </ScrollableContent>
      <NextButtonContainer>
        <NextButton
          type="button"
          onClick={() => {
            formik.setTouched({
              customerId: true,
              billingAddressId: true,
              deliveryAddressId: true,
              scheduledDeliveryDate: true,
              deliveryNote: true,
            });
            formik.submitForm();
          }}
        >
          Next
        </NextButton>
      </NextButtonContainer>
    </FormContainer>
  );
};

export default CustomerInformation;
